import { PLAYER } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import type { Player } from "../../common/types.ts";
import { defineView } from "../util/defineView.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { getDraftLotteryProbs } from "../core/draft/draftLottery.ts";
import { getNumToPick } from "../core/draft/genOrder.ts";
import { last, maxBy } from "../../common/utils.ts";
import { bySport } from "../../common/sportFunctions.ts";
import getNumPlayoffTeams from "../core/season/getNumPlayoffTeams.ts";
import type { RouteParams } from "../../ui/router/types.ts";
import { validateAbbrev } from "../util/processInputs.ts";
import { player } from "../core/index.ts";

const processInputs = (params: RouteParams<"draftTeamHistory">) => {
	let [tid, abbrev] = validateAbbrev(params.abbrev);

	if (params.abbrev === "your_teams") {
		tid = -1;
		abbrev = "your_teams";
	}

	return {
		tid,
		abbrev,
	};
};

export default defineView({
	id: "draftTeamHistory",
	processInputs,
	load: async ({ inputs }) => {
		let filter;
		if (inputs.tid >= 0) {
			filter = (p: Player) => p.draft.tid === inputs.tid;
		} else {
			filter = (p: Player) =>
				p.draft.tid === g.get("userTid", p.draft.year + 1);
		}

		const stats = bySport({
			baseball: ["gp", "keyStats", "war"],
			basketball: ["gp", "min", "pts", "trb", "ast", "per", "ws"],
			football: ["gp", "keyStats", "av"],
			hockey: ["gp", "keyStats", "ops", "dps", "ps"],
		} as const);
		const formatOptions = {
			attrs: [
				"tid",
				"abbrev",
				"draft",
				"pid",
				"firstName",
				"lastName",
				"age",
				"ageAtDeath",
				"hof",
				"watch",
				"jerseyNumber",
				"awards",
				"born",
			],
			ratings: ["ovr", "pot", "skills", "pos", "season"],
			stats,
			showNoStats: true,
			showRookies: true,
			fuzz: true,
		} as const;
		const abbrevs = await player.loadAbbrevs(formatOptions);
		const formatPlayer = player.createFormatter(formatOptions, abbrevs);

		const playersAll = (
			await idb.merged.players.getAll(undefined, {
				filter,
				map: formatPlayer,
			})
		).filter((p) => p !== undefined);

		const players = [];
		for (const p of playersAll) {
			const currentPr = last(p.ratings);
			const peakPr = maxBy(p.ratings, "ovr")!;

			let preLotteryRank: number | undefined;
			let lotteryChange: number | undefined;
			let lotteryProb: number | undefined;
			if (p.draft.round === 1) {
				const draftLottery = await idb.merged.draftLotteryResults.get(
					p.draft.year,
				);
				if (draftLottery) {
					const lotteryRowIndex = draftLottery.result.findIndex(
						(row) => row.pick === p.draft.pick,
					);
					if (lotteryRowIndex >= 0) {
						preLotteryRank = lotteryRowIndex + 1;
						lotteryChange = preLotteryRank - p.draft.pick;

						const { numPlayInTeams } = await getNumPlayoffTeams(p.draft.year);
						const numToPick = await getNumToPick(
							draftLottery.draftType ?? "nba1994",
							draftLottery.result.length,
							numPlayInTeams,
						);
						const { probs } = getDraftLotteryProbs(
							draftLottery,
							draftLottery.draftType,
							numToPick,
						);
						if (probs) {
							lotteryProb = probs[lotteryRowIndex]?.[p.draft.pick - 1];
						}
					}
				}
			}

			players.push({
				// Attributes
				pid: p.pid,
				firstName: p.firstName,
				lastName: p.lastName,
				draft: p.draft,
				currentAge: p.age,
				ageAtDeath: p.ageAtDeath,
				currentAbbrev: p.abbrev,
				currentTid: p.tid,
				hof: p.hof,
				watch: p.watch,
				jerseyNumber: p.jerseyNumber,
				awards: p.awards,

				// Ratings
				currentOvr: p.tid !== PLAYER.RETIRED ? currentPr.ovr : null,
				currentPot: p.tid !== PLAYER.RETIRED ? currentPr.pot : null,
				currentSkills: p.tid !== PLAYER.RETIRED ? currentPr.skills : [],
				pos: currentPr.pos,

				peakAge: peakPr.season - p.born.year,
				peakOvr: peakPr.ovr,
				peakPot: peakPr.pot,
				peakSkills: peakPr.skills,

				// Stats
				careerStats: p.careerStats,

				// Draft lottery
				preLotteryRank,
				lotteryChange,
				lotteryProb,
			});
		}

		return {
			abbrev: inputs.abbrev,
			players: addFirstNameShort(players),
			stats,
			tid: inputs.tid,
		};
	},
});
